import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { STATUS_LABEL, qrCodeSchema } from '@helpdesk/shared';
import { ApiRequestError, getJson, isOfflineError } from '../api/client.ts';
import { machineLookupQuery, meQuery } from '../api/queries.ts';
import type { MachineDetail, MachineLookupItem, PublicMachine, RecentTicket } from '../api/types.ts';
import { MachineLightBadge, PriorityBadge } from '../components/badges.tsx';
import { DuplicateHint } from '../components/duplicate-hint.tsx';
import { PublicShell } from '../components/layout.tsx';
import { ForcedPasswordChange } from '../components/password.tsx';
import { NEXT_STEP_LINK, ReportDone, ReportForm, type ReportResult, type ReportTarget } from '../components/report-form.tsx';
import { ErrorNote, Notice, Spinner, StaleNote } from '../components/ui.tsx';
import { fmtAge } from '../lib/format.ts';
import { LoginPage } from './login.tsx';

/**
 * Einstieg per QR-Code (§10.2): Maschinenseite in unter 2 s, Ampel, letzte
 * Tickets (ohne Namen und Freitext), großer Knopf „Störung melden“.
 * Angemeldet → volles Formular; sonst Schnellmeldung (nur im Firmennetz).
 * Ohne Netz genügt der Code aus der Adresse: Die Meldung wartet im Ausgang, der Server
 * löst ihn beim Senden auf. Den Rest über die Maschine weiß erst die Antwort.
 * Die Routen hängen nicht am `AppLayout`: Das Startpasswort fangen sie selbst ab,
 * der Server sperrt mit ihm jede Abfrage der Maschine.
 */
function useMachineByCode(code: string, stayAnonymous = false) {
  const me = useQuery(meQuery);
  const loggedIn = Boolean(me.data) && !stayAnonymous;
  const data = useQuery({
    queryKey: ['machine-by-code', code, loggedIn],
    enabled: !me.isPending && !me.data?.user.mustChangePassword,
    retry: false,
    // Die Rückkehr aus der Kamera-App zählt als Fokus: Mitten in der Meldung bringt ein neuer Abruf nichts.
    // Nach einem Fehlschlag schon: So kommt zurück, wer zum Melden ins Hallen-WLAN gewechselt ist.
    refetchOnWindowFocus: (query) => query.state.status === 'error',
    queryFn: async (): Promise<PublicMachine> => {
      if (loggedIn) {
        const d = await getJson<MachineDetail & { recent: RecentTicket[] }>(`/api/machines/by-code/${code}`);
        return {
          machine: {
            id: d.id,
            name: d.name,
            inventoryNo: d.inventoryNo,
            qrCode: d.qrCode,
            status: d.status,
            safetyNote: d.safetyNote,
            areaId: d.area.id,
            areaName: d.area.name,
            siteCode: d.site.code,
            siteName: d.site.name,
            light: d.light,
            photo: d.photo,
          },
          recent: d.recent,
          quickEnabled: true,
          sharedDevice: Boolean(me.data?.user.deviceId),
        };
      }
      return getJson<PublicMachine>(`/api/quick/m/${code}`);
    },
  });
  const list = useQuery({ ...machineLookupQuery, enabled: loggedIn }).data;
  // Die Liste kommt mit der Anmeldung: Wer ohne meldet, sieht auch keinen Rest davon.
  const listed = loggedIn ? list?.find((m) => m.qrCode === code) : undefined;
  // Wartet `me` selbst auf das Netz, läuft `data` gar nicht erst los.
  const unanswered = (me.isPending && me.isPaused) || data.isPaused || isOfflineError(data.error);
  const offline = !data.data && unanswered && qrCodeSchema.safeParse(code).success;
  // Ist `me` einmal gescheitert, fällt es bei jedem neuen Versuch auf pending zurück.
  // Die Seiten warten deshalb nur, solange `data.data` fehlt.
  return { me, data, loggedIn, listed, offline };
}

const isQuickNetworkBlock = (error: unknown) => error instanceof ApiRequestError && error.code === 'quick_network';

const LOGIN_REASON = {
  network: 'Melden ohne Anmeldung geht nur aus dem Firmennetz (Hallen-WLAN). Mit Ihrem Arnold-Konto geht es überall.',
  'site-off': 'Melden ohne Anmeldung ist an diesem Standort abgeschaltet. Bitte anmelden.',
} as const;

function LoginNeeded({ reason, onLogin }: { reason: keyof typeof LOGIN_REASON; onLogin: () => void }) {
  return (
    <div className="ad-card space-y-3">
      <h1 className="text-xl">Bitte anmelden</h1>
      <p>{LOGIN_REASON[reason]}</p>
      <button type="button" className="ad-big-button cursor-pointer" onClick={onLogin}>
        Anmelden
      </button>
    </div>
  );
}

function MachineHead({ m }: { m: PublicMachine['machine'] }) {
  return (
    <div className="ad-card space-y-2">
      {m.photo ? (
        // Zum Abgleich vor Ort: ist das die Maschine, vor der ich stehe?
        <img src={m.photo.thumbUrl} alt={`Foto: ${m.name}`} className="aspect-[4/3] w-full max-w-[360px] border border-[var(--border-default)] object-cover" />
      ) : null}
      <div className="ad-section-title">
        {m.siteName} · {m.areaName}
      </div>
      <h1 className="text-[26px] normal-case">{m.name}</h1>
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm text-steel-600">{m.inventoryNo}</span>
        <MachineLightBadge light={m.light} large />
      </div>
      {m.safetyNote ? (
        <div className="border-l-4 border-arnold-red bg-arnold-red-tint px-3 py-2 text-sm">
          <strong>Sicherheitshinweis:</strong> {m.safetyNote}
        </div>
      ) : null}
    </div>
  );
}

function OfflineMachine({ code, listed }: { code: string; listed: MachineLookupItem | undefined }) {
  return (
    <>
      <div className="ad-card space-y-2">
        {listed ? (
          <div className="ad-section-title">
            {listed.siteCode} · {listed.areaName}
          </div>
        ) : null}
        <h1 className="text-[26px] normal-case">{listed?.name ?? `Maschine ${code}`}</h1>
        {listed ? <span className="font-mono text-sm text-steel-600">{listed.inventoryNo}</span> : null}
      </div>
      <Notice tone="warn">Kein Netz. Sicherheitshinweis, Ampel und letzte Meldungen erscheinen erst mit Verbindung. Melden geht trotzdem.</Notice>
      <Link to="/m/$code/melden" params={{ code }} className="ad-big-button">
        Störung melden
      </Link>
    </>
  );
}

function RecentList({ recent }: { recent: RecentTicket[] }) {
  if (!recent.length) return <p className="text-sm text-[var(--text-muted)]">Keine Meldungen zu dieser Maschine.</p>;
  return (
    <ul className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] bg-white">
      {recent.map((t) => (
        <li key={t.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
          <span className="font-mono text-sm">{t.number}</span>
          <PriorityBadge priority={t.priority} short />
          <span className="text-sm">{t.needsReview && t.isOpen ? 'gemeldet, ungeprüft' : STATUS_LABEL[t.status]}</span>
          <span className="ml-auto text-[13px] text-[var(--text-muted)]">{fmtAge(t.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}

export function MachinePublicPage() {
  const { code } = useParams({ from: '/m/$code' });
  const upper = code.toUpperCase();
  const { me, data, loggedIn, listed, offline } = useMachineByCode(upper);
  const [loginOpen, setLoginOpen] = useState(false);

  if (me.data?.user.mustChangePassword) return <ForcedPasswordChange user={me.data.user} />;
  if (loginOpen && !loggedIn) return <LoginPage onBack={() => setLoginOpen(false)} />;

  return (
    <PublicShell>
      <div className="space-y-5">
        {offline ? (
          <OfflineMachine code={upper} listed={listed} />
        ) : !data.data && (me.isPending || data.isPending) ? (
          <Spinner />
        ) : isQuickNetworkBlock(data.error) ? (
          <LoginNeeded reason="network" onLogin={() => setLoginOpen(true)} />
        ) : data.isLoadingError ? (
          <ErrorNote error={data.error} />
        ) : (
          <>
            <StaleNote query={data} />
            <MachineHead m={data.data.machine} />
            {data.data.machine.status !== 'active' ? (
              <Notice tone="warn">Diese Maschine ist stillgelegt.</Notice>
            ) : loggedIn ? (
              <Link to="/melden" search={{ machineId: data.data.machine.id, via: 'qr' }} className="ad-big-button">
                Störung melden
              </Link>
            ) : data.data.quickEnabled ? (
              <Link to="/m/$code/melden" params={{ code: upper }} className="ad-big-button">
                Störung melden
              </Link>
            ) : (
              <LoginNeeded reason="site-off" onLogin={() => setLoginOpen(true)} />
            )}
            <section className="space-y-2">
              <h2 className="ad-section-title">Letzte Meldungen</h2>
              <RecentList recent={data.data.recent} />
              <p className="text-[13px] text-[var(--text-muted)]">Status Ihrer Meldung? Einfach den QR-Code erneut scannen.</p>
            </section>
            {loggedIn ? (
              <p className="flex flex-wrap gap-x-5 gap-y-2 text-[15px]">
                <Link to="/maschinen/$id" params={{ id: data.data.machine.id }}>
                  Maschinenakte öffnen
                </Link>
                <Link to="/">Zur Startseite</Link>
              </p>
            ) : null}
          </>
        )}
      </div>
    </PublicShell>
  );
}

export function MachineQuickReportPage() {
  const { code } = useParams({ from: '/m/$code/melden' });
  const upper = code.toUpperCase();
  // Das offene Formular hält sein Ziel fest: Ein anderer Code muss es neu aufbauen, sonst ginge die Meldung an die alte Maschine.
  return <QuickReport key={upper} upper={upper} />;
}

function QuickReport({ upper }: { upper: string }) {
  const [opened, setOpened] = useState<ReportTarget | null>(null);
  const { me, data, loggedIn, listed, offline } = useMachineByCode(upper, opened?.kind === 'quick');
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const quick: ReportTarget = { kind: 'quick', code: upper };
  const offlineTarget: ReportTarget =
    loggedIn && me.data ? { kind: 'web', machineId: null, machineCode: upper, withoutMachine: false, origin: 'qr', owner: me.data.user } : quick;
  const target = opened ?? (offline ? offlineTarget : !loggedIn && data.data?.quickEnabled ? quick : null);
  const anonymous = target?.kind === 'quick';

  // Eine erst jetzt durchkommende Anmeldung oder Antwort würde Schlüssel, Weiterleitung und Zweig kippen und die Eingaben abräumen.
  useEffect(() => {
    if (target) setOpened(target);
  }, [target]);

  if (me.data?.user.mustChangePassword) return <ForcedPasswordChange user={me.data.user} />;
  if (!target && loggedIn && data.data) return <Navigate to="/melden" search={{ machineId: data.data.machine.id, via: 'qr' }} replace />;
  if (loginOpen && !loggedIn) return <LoginPage onBack={() => setLoginOpen(false)} />;

  const shown = data.data?.machine ?? listed;

  return (
    <PublicShell>
      <div className="space-y-5">
        {result ? (
          <ReportDone
            result={result}
            onAgain={() => setResult(null)}
            extra={() => (
              <>
                <a href={`/m/${upper}`} className={NEXT_STEP_LINK}>
                  Zur Maschinenseite (Status)
                </a>
                {/* Ausgang und Anmeldung gibt es nur in der App, dieser Rahmen hat keinen Kopf dafür. */}
                {target?.kind === 'web' ? (
                  <Link to="/" className={NEXT_STEP_LINK}>
                    Zur App
                  </Link>
                ) : null}
              </>
            )}
          />
        ) : target ? (
          <>
            <h1 className="text-2xl">Störung melden</h1>
            {data.data ? null : (
              <Notice tone="warn">
                Kein Netz. Sicherheitshinweis und offene Meldungen zu dieser Maschine erscheinen erst mit Verbindung. Melden geht trotzdem: Die Meldung bleibt auf diesem Gerät und geht an diese Maschine,{' '}
                {anonymous ? 'sobald diese Seite im Firmennetz (Hallen-WLAN) offen ist.' : 'sobald die App wieder Verbindung hat.'}
              </Notice>
            )}
            {data.data ? <DuplicateHint tickets={data.data.recent} code={upper} anonymous={anonymous} sharedDevice={data.data.sharedDevice} /> : null}
            <ReportForm
              target={target}
              anonymous={anonymous}
              machineLabel={shown ? `${shown.name} · ${shown.inventoryNo}` : `Code ${upper}`}
              safetyNote={data.data?.machine.safetyNote}
              sharedDevice={data.data?.sharedDevice}
              onDone={setResult}
            />
          </>
        ) : !data.data && (me.isPending || data.isPending) ? (
          <Spinner />
        ) : data.isLoadingError ? (
          isQuickNetworkBlock(data.error) ? <LoginNeeded reason="network" onLogin={() => setLoginOpen(true)} /> : <ErrorNote error={data.error} />
        ) : data.data.machine.status !== 'active' ? (
          <>
            <Notice tone="warn">Diese Maschine ist stillgelegt.</Notice>
            <Link to="/m/$code" params={{ code: upper }} className="block">
              Zur Maschinenseite
            </Link>
          </>
        ) : (
          <LoginNeeded reason="site-off" onLogin={() => setLoginOpen(true)} />
        )}
      </div>
    </PublicShell>
  );
}
